import React, { Component } from 'react';
import { Brain, AlertTriangle, Activity, TrendingDown } from 'lucide-react';
import { motion } from 'framer-motion';
export const HiddenToxicitySection = () => {
  return <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden" data-id="element-2696">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" data-id="element-2697" />

      {/* Red Glow Accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FF0000]/5 rounded-full blur-[120px] pointer-events-none" data-id="element-2698" />

      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-10" data-id="element-2699">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16 md:mb-20" data-id="element-2700">
          <div className="inline-flex items-center gap-2 mb-6 bg-[#FF0000]/10 border border-[#FF0000]/30 px-4 py-2 rounded-full" data-id="element-2701">
            <Brain size={16} className="text-[#FF0000]" data-id="element-2702" />
            <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-2703">
              Neurological Research
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 uppercase tracking-tight leading-none px-4" data-id="element-2704">
            The <span className="text-[#FF0000]" data-id="element-2705">Hidden Toxicity</span> Crisis
          </h2>

          <p className="text-base md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed font-mono px-4" data-id="element-2706">
            Why your brain can't detect the patterns that are destroying you.
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-16 md:mb-20" data-id="element-2707">
          {/* Left: The Problem */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-2708">
            <div className="relative bg-[#0a0a0a] border border-gray-800 p-6 md:p-8 h-full" data-id="element-2709">
              {/* Corner Brackets */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#FF0000]" data-id="element-2710" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#FF0000]" data-id="element-2711" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#FF0000]" data-id="element-2712" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#FF0000]" data-id="element-2713" />

              <div className="flex items-center gap-3 mb-8" data-id="element-2714">
                <div className="w-12 h-12 bg-[#FF0000]/10 border border-[#FF0000] flex items-center justify-center flex-shrink-0" data-id="element-2715">
                  <AlertTriangle size={24} className="text-[#FF0000]" data-id="element-2716" />
                </div>
                <div className="min-w-0" data-id="element-2717">
                  <div className="text-[10px] font-mono text-gray-500 tracking-widest uppercase" data-id="element-2718">
                    Diagnostic Finding
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight" data-id="element-2719">
                    The Problem
                  </h3>
                </div>
              </div>

              <div className="space-y-6" data-id="element-2720">
                <div data-id="element-2721">
                  <div className="text-sm font-mono text-[#FFD700] mb-3 uppercase tracking-wide" data-id="element-2722">
                    What You Think:
                  </div>
                  <p className="text-gray-300 text-base md:text-lg leading-relaxed" data-id="element-2723">
                    "We're just having normal relationship conflicts."
                  </p>
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent" data-id="element-2724" />

                <div data-id="element-2725">
                  <div className="text-sm font-mono text-[#FF0000] mb-3 uppercase tracking-wide flex items-center gap-2" data-id="element-2726">
                    <span className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse flex-shrink-0" data-id="element-2727" />
                    Clinical Reality:
                  </div>
                  <p className="text-white text-base md:text-lg leading-relaxed font-medium" data-id="element-2728">
                    You're experiencing systematic emotional drainage through
                    patterns your brain has been conditioned to normalize.
                  </p>
                </div>

                <div className="bg-[#FF0000]/5 border border-[#FF0000]/20 p-4 rounded" data-id="element-2729">
                  <div className="text-xs font-mono text-[#FF0000] mb-2 uppercase tracking-widest" data-id="element-2730">
                    ⚠ Critical Insight
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed font-mono" data-id="element-2731">
                    Your nervous system adapts to chronic stress by
                    <span className="text-white font-bold" data-id="element-2732">
                      {' '}
                      normalizing the abuse
                    </span>
                    . This is called "trauma bonding" or "acclimatization to
                    toxicity."
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: The Science */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} data-id="element-2733">
            <div className="relative bg-[#0a0a0a] border border-gray-800 p-6 md:p-8 h-full" data-id="element-2734">
              {/* Corner Brackets */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-gray-600" data-id="element-2735" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-gray-600" data-id="element-2736" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-gray-600" data-id="element-2737" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-gray-600" data-id="element-2738" />

              <div className="flex items-center gap-3 mb-8" data-id="element-2739">
                <div className="w-12 h-12 bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0" data-id="element-2740">
                  <Activity size={24} className="text-gray-400" data-id="element-2741" />
                </div>
                <div className="min-w-0" data-id="element-2742">
                  <div className="text-[10px] font-mono text-gray-500 tracking-widest uppercase" data-id="element-2743">
                    Neurological Mechanism
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight" data-id="element-2744">
                    The Science
                  </h3>
                </div>
              </div>

              <div className="space-y-6" data-id="element-2745">
                <div data-id="element-2746">
                  <h4 className="text-base md:text-lg font-bold text-white mb-4 uppercase tracking-wide" data-id="element-2747">
                    Why You Can't See It:
                  </h4>
                  <ul className="space-y-3" data-id="element-2748">
                    <SciencePoint number="01" text="Your amygdala (threat detector) becomes desensitized to chronic manipulation" data-id="element-2749" />
                    <SciencePoint number="02" text="Cortisol flooding creates cognitive fog that impairs pattern recognition" data-id="element-2750" />
                    <SciencePoint number="03" text="Intermittent reinforcement (hot/cold cycles) creates addiction-like neural pathways" data-id="element-2751" />
                  </ul>
                </div>

                <div className="bg-gray-900/50 border border-gray-800 p-4 rounded" data-id="element-2752">
                  <div className="flex items-center justify-between mb-4" data-id="element-2753">
                    <span className="text-xs font-mono text-gray-500 uppercase tracking-widest" data-id="element-2754">
                      Threat Level Over Time
                    </span>
                    <TrendingDown size={16} className="text-[#FF0000]" data-id="element-2755" />
                  </div>

                  {/* Simple Data Visualization */}
                  <div className="space-y-3" data-id="element-2756">
                    <ProgressBar label="Week 1" percentage={85} color="bg-[#FFD700]" data-id="element-2757" />
                    <ProgressBar label="Week 4" percentage={60} color="bg-orange-500" data-id="element-2758" />
                    <ProgressBar label="Week 12" percentage={30} color="bg-[#FF0000]" data-id="element-2759" />
                  </div>

                  <p className="text-xs text-gray-500 mt-4 font-mono leading-relaxed" data-id="element-2760">
                    Your brain's threat detection decreases as it adapts to
                    chronic toxicity.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom: The Hook */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="relative" data-id="element-2761">
          <div className="bg-gradient-to-r from-[#FF0000]/10 via-[#FF0000]/5 to-[#FF0000]/10 border-l-4 border-[#FF0000] p-6 md:p-8 lg:p-12" data-id="element-2762">
            <div className="max-w-4xl" data-id="element-2763">
              <div className="flex items-start gap-4 md:gap-6 mb-8" data-id="element-2764">
                <div className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 bg-[#FF0000] flex items-center justify-center text-white font-black text-xl md:text-2xl" data-id="element-2765">
                  !
                </div>
                <div className="min-w-0" data-id="element-2766">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-white mb-4 uppercase tracking-tight leading-tight" data-id="element-2767">
                    You're Not "Just Fighting."
                    <br data-id="element-2768" />
                    <span className="text-[#FF0000]" data-id="element-2769">
                      You're Being Systematically Drained.
                    </span>
                  </h3>
                  <p className="text-gray-300 text-base md:text-lg leading-relaxed" data-id="element-2770">
                    Most people don't realize they're victims of emotional
                    manipulation until they've lost their sense of self. The
                    patterns are invisible because{' '}
                    <span className="text-white font-bold" data-id="element-2771">
                      your brain has been programmed to ignore them
                    </span>
                    .
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2772">
                <StatBox number="73%" label="of victims don't recognize gaslighting until after the relationship ends" data-id="element-2773" />
                <StatBox number="2-5 yrs" label="average time to identify covert narcissistic abuse" data-id="element-2774" />
                <StatBox number="89%" label="report feeling 'crazy' or doubting their own reality" data-id="element-2775" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
};
// Helper Components
const SciencePoint = ({
  number,
  text
}: {
  number: string;
  text: string;
}) => <li className="flex items-start gap-3" data-id="element-2776">
    <span className="flex-shrink-0 w-8 h-8 bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 font-mono text-xs" data-id="element-2777">
      {number}
    </span>
    <span className="text-gray-300 text-sm leading-relaxed pt-1.5" data-id="element-2778">{text}</span>
  </li>;
const ProgressBar = ({
  label,
  percentage,
  color
}: {
  label: string;
  percentage: number;
  color: string;
}) => <div data-id="element-2779">
    <div className="flex justify-between text-xs font-mono text-gray-500 mb-2" data-id="element-2780">
      <span data-id="element-2781">{label}</span>
      <span data-id="element-2782">{percentage}% awareness</span>
    </div>
    <div className="h-2 bg-gray-800 overflow-hidden rounded-sm" data-id="element-2783">
      <motion.div initial={{
      width: 0
    }} whileInView={{
      width: `${percentage}%`
    }} viewport={{
      once: true
    }} transition={{
      duration: 1,
      delay: 0.5,
      ease: 'easeOut'
    }} className={`h-full ${color}`} data-id="element-2784" />
    </div>
  </div>;
const StatBox = ({
  number,
  label
}: {
  number: string;
  label: string;
}) => <div className="bg-[#0a0a0a] border border-gray-800 p-4 md:p-5 text-center rounded" data-id="element-2785">
    <div className="text-2xl md:text-3xl font-black text-[#FF0000] mb-2 font-mono" data-id="element-2786">
      {number}
    </div>
    <div className="text-xs text-gray-400 leading-tight font-mono uppercase" data-id="element-2787">
      {label}
    </div>
  </div>;